import React from 'react';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Table, Tag, Space } from 'antd';
import { FastingLogDto, FastingPlanType, FastingStatus } from '@wellverse/shared-ts';

const statusColors: Record<FastingStatus, string> = {
  [FastingStatus.ACTIVE]: '#1890ff',
  [FastingStatus.COMPLETED]: '#3ecf8e',
  [FastingStatus.BROKEN]: '#ff6b6b',
};

const planTypeColors: Record<FastingPlanType, string> = {
  [FastingPlanType.SIXTEEN_EIGHT]: '#722ed1',
  [FastingPlanType.EIGHTEEN_SIX]: '#13c2c2',
  [FastingPlanType.TWENTY_FOUR]: '#fa8c16',
  [FastingPlanType.OMAD]: '#eb2f96',
  [FastingPlanType.CUSTOM]: '#8c8c8c',
};

export const FastingLogList: React.FC = () => {
  const { tableProps } = useTable<FastingLogDto>({
    resource: 'fasting-logs',
    pagination: { current: 1, pageSize: 20 },
  });

  return (
    <List resource="fasting-logs">
      <Table {...tableProps} rowKey="id">
        <Table.Column dataIndex="userId" title="User ID" />
        <Table.Column
          dataIndex="planType"
          title="Plan Type"
          render={(planType: FastingPlanType) => (
            <Tag
              style={{
                borderColor: planTypeColors[planType] || '#898989',
                color: planTypeColors[planType] || '#898989',
                background: `${planTypeColors[planType] || '#898989'}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {planType.replace('_', ' ')}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: FastingStatus) => (
            <Tag
              style={{
                borderColor: statusColors[status] || '#898989',
                color: statusColors[status] || '#898989',
                background: `${statusColors[status] || '#898989'}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {status}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="startTime"
          title="Start Time"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="endTime"
          title="End Time"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="actualDurationMinutes"
          title="Duration"
          render={(minutes: number | undefined, record: FastingLogDto) => {
            if (minutes !== undefined && minutes !== null) {
              const hours = Math.floor(minutes / 60);
              const mins = minutes % 60;
              return `${hours}h ${mins}m`;
            }
            if (record.status === FastingStatus.ACTIVE) {
              const now = Date.now();
              const startMs = new Date(record.startTime).getTime();
              const elapsed = Math.floor((now - startMs) / (60 * 1000));
              const hours = Math.floor(elapsed / 60);
              const mins = elapsed % 60;
              return `${hours}h ${mins}m (ongoing)`;
            }
            return `${record.targetDurationMinutes}m (target)`;
          }}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_, record: FastingLogDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="fasting-logs" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};